import { EventEmitter, Injectable, Injector } from "@angular/core";
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from "@angular/material/legacy-dialog";
import { AppService } from "@app/app.service";
import { shouldShowBulkEditProgress } from "@app/components/bulk-edit-progress-overlay/bulk-edit-loading.util";
import type { BulkEditProgressMode } from "@app/components/bulk-edit-progress-overlay/bulk-edit-progress-overlay.component";
import { BulkOperationProgressDialogComponent } from "@app/components/bulk-operation-progress-dialog/bulk-operation-progress-dialog.component";
import { SONNER_DEFAULT_CONFIG, SONNER_ERROR_TOAST_DURATION_MS } from "@app/config/sonner";
import { CreateUndoService } from "@app/services/create-undo/create-undo.service";
import { EditUndoService } from "@app/services/edit-undo/edit-undo.service";
import { MixedTimesSyncService } from "@app/services/mixed-times-sync.service";
import { dismissTimeEntryUndoToast, TIME_ENTRY_UNDO_TOAST_ID } from "@app/services/time-entry-undo-toast.util";
import { RecordToolbarService } from "@app/shared/record-toolbar/record-toolbar.service";
import { environment } from "@env/environment";
import { UntilDestroy } from "@ngneat/until-destroy";
import { TranslateService } from "@ngx-translate/core";
import { ComegoService, MyTimesQuery, MyTimesService, Time, UserSettingsQuery } from "@timeghost/web-api";
import { MyTimesStore } from "@timeghost/web-api/lib/stores/myTimes/myTimes.store";
import { format } from "date-fns/esm";
import { merge } from "lodash-es";
import { ExternalToast, toast } from "ngx-sonner";
import { firstValueFrom, of } from "rxjs";
import { catchError, finalize } from "rxjs/operators";
import type { ComegoAddPayload, ComegoEntryLike, DeletionEvent, DeletionRequest } from "./deletion-undo.types";

export type { DeletionEvent, DeletionRequest } from "./deletion-undo.types";

const UNDO_TIMEOUT_MS = 12000;
const DELETE_CHUNK_SIZE = 50;
const RESTORE_CHUNK_SIZE = 50;

function toIsoString(val: Date | string): string {
	return val instanceof Date ? val.toISOString() : String(val);
}

@UntilDestroy()
@Injectable({
	providedIn: "root",
})
export class DeletionUndoService {
	private pendingDeletions = new Map<string, { toastId: string; restore: () => void }>();

	/** Emitted after a successful delete (before undo). Filter by type if needed. */
	readonly deleted = new EventEmitter<DeletionEvent>(true);

	/** Emitted after a successful restore via undo. Filter by type if needed. */
	readonly restored = new EventEmitter<DeletionEvent>(true);

	private get store(): MyTimesStore {
		return this.myTimesQuery.__store__ as MyTimesStore;
	}

	constructor(
		private myTimesQuery: MyTimesQuery,
		private myTimesService: MyTimesService,
		private comegoService: ComegoService,
		private appService: AppService,
		private recordService: RecordToolbarService,
		private translateService: TranslateService,
		private userSettingsQuery: UserSettingsQuery,
		private dialog: MatDialog,
		private injector: Injector,
		private mixedTimesSyncService: MixedTimesSyncService
	) {}

	/** Clears tracked undo state and dismisses the shared undo toast. */
	dismissPendingUndoToasts(): void {
		dismissTimeEntryUndoToast();
		this.pendingDeletions.clear();
	}

	/** Dismisses all time-entry undo toasts (create, edit, and delete). */
	dismissAllPendingUndoToasts(): void {
		this.dismissPendingUndoToasts();
		this.injector.get(CreateUndoService).dismissPendingUndoToasts();
		this.injector.get(EditUndoService).dismissPendingUndoToasts();
	}

	deleteWithUndo(request: DeletionRequest): void | Promise<void> {
		switch (request.type) {
			case "timeEntry":
				this.deleteTimeEntry(request);
				break;
			case "timeEntries":
				this.deleteTimeEntries(request);
				break;
			case "comegoDay":
				return this.deleteComegoDay(request);
		}
	}

	private deleteTimeEntry(request: Extract<DeletionRequest, { type: "timeEntry" }>): void {
		const { entry } = request;
		const key = entry.id;

		this.dismissAllPendingUndoToasts();

		this.myTimesService
			.delete(entry)
			.pipe(
				catchError((err) => {
					this.recordService.handleError(err);
					return of(null);
				}),
				finalize(() => {
					this.store.remove(entry.id);
				})
			)
			.subscribe({
				next: (result) => {
					if (result !== null) {
						void this.mixedTimesSyncService.handleProjectTimeDelete(entry);
						const restore = () => this.restoreTimeEntry(entry);
						toast.success(
							null,
							merge({}, { ...SONNER_DEFAULT_CONFIG }, {
								id: TIME_ENTRY_UNDO_TOAST_ID,
								duration: UNDO_TIMEOUT_MS,
								description: this.translateService.instant("success.deleted"),
								action: {
									label: this.translateService.instant("utils.undo"),
									onClick: () => {
										if (!this.pendingDeletions.has(key)) return;
										restore();
									},
								},
								onDismiss: () => this.pendingDeletions.delete(key),
								onAutoClose: () => this.pendingDeletions.delete(key),
							} as ExternalToast)
						);
						this.pendingDeletions.set(key, { toastId: TIME_ENTRY_UNDO_TOAST_ID, restore });
						this.deleted.emit({ key, type: "timeEntry" });
					}
				},
			});
	}

	private deleteTimeEntries(request: Extract<DeletionRequest, { type: "timeEntries" }>): void {
		const { entries } = request;
		if (!entries?.length) return;
		void this.deleteTimeEntriesWithProgress(entries);
	}

	private async deleteTimeEntriesWithProgress(entries: Time[]): Promise<void> {
		this.dismissAllPendingUndoToasts();

		const key = `timeEntries-${Date.now()}`;
		const loadingRef = this.openBulkOperationProgressDialog(entries.length, "delete");
		loadingRef?.componentInstance.setProgress(0);

		const deletedEntries: Time[] = [];
		let processed = 0;
		let failed = 0;

		try {
			this.store.setError(null);
			for (const chunk of this.chunkArray(entries, DELETE_CHUNK_SIZE)) {
				const chunkDeleted = await this.deleteChunk(chunk);
				if (chunkDeleted.length) {
					deletedEntries.push(...chunkDeleted);
					chunkDeleted.forEach((entry) => {
						this.store.remove(entry.id);
						void this.mixedTimesSyncService.handleProjectTimeDelete(entry);
					});
				}
				processed += chunk.length;
				failed += chunk.length - chunkDeleted.length;
				loadingRef?.componentInstance.setProgress(processed, failed);
			}

			const deletedCount = deletedEntries.length;
			const failedCount = entries.length - deletedCount;

			if (!deletedCount) {
				toast.error(
					null,
					merge({}, { ...SONNER_DEFAULT_CONFIG }, {
						duration: SONNER_ERROR_TOAST_DURATION_MS,
						description: this.translateService.instant("time.bulk-delete-partial-failed", {
							count: failedCount,
						}),
					} as ExternalToast)
				);
				return;
			}

			const restore = () => this.restoreTimeEntries(key, deletedEntries);
			this.showBulkDeleteResultToast(deletedCount, failedCount, restore, key);
			this.pendingDeletions.set(key, { toastId: TIME_ENTRY_UNDO_TOAST_ID, restore });
			this.deleted.emit({ key, type: "timeEntries" });
		} finally {
			this.store.setError(null);
			loadingRef?.close();
		}
	}

	/** Bypasses `MyTimesService.delete` so failed requests do not call `myTimesStore.setError` per entry. */
	private deleteTimesByIds(ids: string[]): Promise<unknown> {
		if (!ids.length) return Promise.resolve(null);
		return firstValueFrom(
			this.myTimesService.adapter
				.delete(`${environment.serverUrl}/delete/times/${ids.join(";")}`)
				.pipe(catchError(() => of(null)))
		);
	}

	private showBulkDeleteResultToast(
		deletedCount: number,
		failedCount: number,
		restore: () => void,
		key: string
	): void {
		if (failedCount > 0) {
			toast.error(
				null,
				merge({}, { ...SONNER_DEFAULT_CONFIG }, {
					duration: SONNER_ERROR_TOAST_DURATION_MS,
					description: this.translateService.instant("time.bulk-delete-partial-failed", {
						count: failedCount,
					}),
				} as ExternalToast)
			);
		}

		const description =
			failedCount > 0
				? this.translateService.instant("time.bulk-delete-partial-success", { count: deletedCount })
				: this.translateService.instant("success.deleted-plural");

		toast.success(
			null,
			merge({}, { ...SONNER_DEFAULT_CONFIG }, {
				id: TIME_ENTRY_UNDO_TOAST_ID,
				duration: UNDO_TIMEOUT_MS,
				description,
				action: {
					label: this.translateService.instant("utils.undo"),
					onClick: () => {
						if (!this.pendingDeletions.has(key)) return;
						restore();
					},
				},
				onDismiss: () => this.pendingDeletions.delete(key),
				onAutoClose: () => this.pendingDeletions.delete(key),
			} as ExternalToast)
		);
	}

	private openBulkOperationProgressDialog(
		total: number,
		mode: BulkEditProgressMode
	): MatDialogRef<BulkOperationProgressDialogComponent> | undefined {
		if (!shouldShowBulkEditProgress(total)) return undefined;
		return this.dialog.open(BulkOperationProgressDialogComponent, {
			data: { total, mode },
			disableClose: true,
			closeOnNavigation: false,
		});
	}

	private async deleteChunk(chunk: Time[]): Promise<Time[]> {
		const ids = chunk.map((entry) => entry.id);
		const batchResult = await this.deleteTimesByIds(ids);
		if (batchResult !== null) return chunk;

		const deleted: Time[] = [];
		for (const entry of chunk) {
			const singleResult = await this.deleteTimesByIds([entry.id]);
			if (singleResult !== null) deleted.push(entry);
		}
		return deleted;
	}

	private chunkArray<T>(arr: T[], size: number): T[][] {
		const chunks: T[][] = [];
		for (let i = 0; i < arr.length; i += size) {
			chunks.push(arr.slice(i, i + size));
		}
		return chunks;
	}

	private restoreTimeEntries(key: string, entries: Time[]): void {
		void this.restoreTimeEntriesWithProgress(key, entries);
	}

	private async restoreTimeEntriesWithProgress(key: string, entries: Time[]): Promise<void> {
		const pending = this.pendingDeletions.get(key);
		if (pending) {
			dismissTimeEntryUndoToast();
			this.pendingDeletions.delete(key);
		}

		const loadingRef = this.openBulkOperationProgressDialog(entries.length, "undo");
		loadingRef?.componentInstance.setProgress(0);

		try {
			// Before confirmProjectTimeCreate: that call queues behind an open correction dialog, so
			// a dialog the restore already settles has to be retired first.
			this.mixedTimesSyncService.closeCorrectionDialogIfResolved(entries, { pendingRestore: true });

			const payloads = entries.map((entry) => this.buildRestorePayload(entry));
			const restored: Time[] = [];
			let processed = 0;
			let failed = 0;

			for (const chunk of this.chunkArray(payloads, RESTORE_CHUNK_SIZE)) {
				const confirmed: typeof payloads = [];
				for (const payload of chunk) {
					const ok = await this.mixedTimesSyncService.confirmProjectTimeCreate(payload);
					if (ok) confirmed.push(payload);
				}
				const chunkRestored = await this.addRestoreChunk(confirmed);
				restored.push(...chunkRestored);
				for (const created of chunkRestored) {
					await this.mixedTimesSyncService.finalizeAfterProjectTimeCreate(created);
				}
				processed += chunk.length;
				failed += chunk.length - chunkRestored.length;
				loadingRef?.componentInstance.setProgress(processed, failed);
			}
			if (restored.length) {
				this.store.upsertMany(restored);
				this.appService.notifySuccess(this.translateService.instant("success.restored-plural"));
				this.restored.emit({ key, type: "timeEntries", entryIds: restored.map((t) => t.id) });
			} else {
				this.appService.notifyError("utils.restore_failed");
			}
		} catch (err) {
			this.recordService.handleError(err);
			this.appService.notifyError("utils.restore_failed");
		} finally {
			loadingRef?.close();
		}
	}

	private buildRestorePayload(entry: Time) {
		return {
			name: entry.name,
			project: { id: entry.project.id },
			task: entry.task,
			billable: !!entry.billable,
			tags: (entry.tags?.filter(({ id, name }) => id && name) ?? []) as any,
			start: new Date(entry.start),
			end: new Date(entry.end),
			timeDiff: entry.timeDiff,
			inputMode: (entry as any)["inputMode"] || "range",
			recordType: (entry as any)["recordType"] || "manual",
		};
	}

	private async addRestoreChunk(payloads: ReturnType<typeof this.buildRestorePayload>[]): Promise<Time[]> {
		if (!payloads.length) return [];

		const result = await firstValueFrom(
			this.myTimesService.add(...(payloads as any[])).pipe(catchError(() => of(null)))
		);
		if (result?.length) return result;

		const restored: Time[] = [];
		for (const payload of payloads) {
			const created = await this.addRestorePayload(payload);
			if (created) restored.push(created);
		}
		return restored;
	}

	private async addRestorePayload(payload: ReturnType<typeof this.buildRestorePayload>): Promise<Time | null> {
		const result = await firstValueFrom(this.myTimesService.add(payload).pipe(catchError(() => of(null))));
		return result?.[0] ?? null;
	}

	private restoreTimeEntry(entry: Time): void {
		const key = entry.id;
		const pending = this.pendingDeletions.get(key);
		if (pending) {
			dismissTimeEntryUndoToast();
			this.pendingDeletions.delete(key);
		}

		const entryToRestore: any = {
			name: entry.name,
			project: { id: entry.project.id },
			task: entry.task,
			billable: !!entry.billable,
			tags: (entry.tags?.filter(({ id, name }) => id && name) ?? []) as any,
			start: new Date(entry.start),
			end: new Date(entry.end),
			timeDiff: entry.timeDiff,
			inputMode: (entry as any)["inputMode"] || "range",
			recordType: (entry as any)["recordType"] || "manual",
		};

		void (async () => {
			// Before confirmProjectTimeCreate: that call queues behind an open correction dialog, so
			// a dialog the restore already settles has to be retired first.
			this.mixedTimesSyncService.closeCorrectionDialogIfResolved(entry, { pendingRestore: true });

			const confirmed = await this.mixedTimesSyncService.confirmProjectTimeCreate(entryToRestore);
			if (!confirmed) return;
			this.myTimesService
				.add(entryToRestore)
				.pipe(
					catchError((err) => {
						this.recordService.handleError(err);
						return of(null);
					})
				)
				.subscribe({
					next: (result: any) => {
						if (result && result[0]) {
							this.store.upsertMany([result[0]]);
							void this.mixedTimesSyncService.finalizeAfterProjectTimeCreate(result[0]);
							this.appService.notifySuccess(this.translateService.instant("success.restored"));
							this.restored.emit({ key, type: "timeEntry" });
						}
					},
					error: () => {
						this.appService.notifyError("utils.restore_failed");
					},
				});
		})();
	}

	private async deleteComegoDay(request: Extract<DeletionRequest, { type: "comegoDay" }>): Promise<void> {
		const { view, day } = request;
		const entries = view.times;
		if (!entries?.length) return;

		const key = view.id;

		try {
			await Promise.all(entries.map((e) => this.comegoService.delete({ id: e.id })));
		} catch (err) {
			this.recordService.handleError(err);
			return;
		}

		const dateFormat = this.userSettingsQuery.getValue().settings?.dateFormat ?? "PP";
		const formattedDay = typeof day === "string" ? day : format(day, dateFormat);

		const restore = () => this.restoreComegoDay(key, entries);
		const toastId = this.appService.notifySuccess(
			"success.deleted-plural",
			{
				duration: UNDO_TIMEOUT_MS,
				action: {
					label: this.translateService.instant("utils.undo"),
					onClick: () => restore(),
				},
				onDismiss: () => this.pendingDeletions.delete(key),
				onAutoClose: () => this.pendingDeletions.delete(key),
			},
			{ day: formattedDay }
		);

		this.pendingDeletions.set(key, { toastId: String(toastId), restore });
		this.deleted.emit({ key, type: "comegoDay" });
	}

	private restoreComegoDay(viewId: string, entries: ComegoEntryLike[]): void {
		const pending = this.pendingDeletions.get(viewId);
		if (!pending) return;

		toast.dismiss(pending.toastId);
		this.pendingDeletions.delete(viewId);

		const payloads: ComegoAddPayload[] = entries.map((e) => ({
			start: toIsoString(e.start),
			end: toIsoString(e.end),
			type: e.type,
			name: e.name ?? "",
			...(e.typeId != null && { typeId: e.typeId }),
			user: e.user?.id ? { id: e.user.id } : undefined,
			...(e.workspace?.id && { workspace: { id: e.workspace.id } }),
		}));

		this.comegoService
			.add(...payloads)
			.then(() => {
				this.appService.notifySuccess("success.restored-plural");
				this.restored.emit({ key: viewId, type: "comegoDay" });
			})
			.catch((err) => {
				this.recordService.handleError(err);
				this.appService.notifyError("utils.restore_failed");
			});
	}

	isPendingDeletion(key: string): boolean {
		return this.pendingDeletions.has(key);
	}

	ngOnDestroy(): void {
		this.dismissPendingUndoToasts();
	}
}
